/**
 * The single action the install-required screen offers, derived from what the
 * platform can actually tell us about install state:
 *   - `installed`   — an installed instance was detected (getInstalledRelatedApps
 *                     match or an appinstalled event) → offer "Open app" only.
 *   - `installable` — the browser fired beforeinstallprompt → offer "Install JioPC" only.
 *   - `detecting`   — Chromium, but neither has resolved yet → brief spinner so
 *                     we never flash the wrong button.
 *   - `manual`      — settled with no prompt and no detection (iOS Safari,
 *                     Firefox, older Chromium) → show manual "add to home screen" steps.
 */
export type InstallScreenState = 'installed' | 'installable' | 'detecting' | 'manual';

export interface InstallSignals {
  /** An installed instance of this PWA was detected. */
  installed: boolean;
  /** The browser offered a programmatic install (beforeinstallprompt captured). */
  canInstall: boolean;
  /** Detection has finished (async lookup resolved and/or the grace period elapsed). */
  settled: boolean;
}

/** Maps the current install signals to the one action the screen should present. */
export function resolveInstallScreenState({ installed, canInstall, settled }: InstallSignals): InstallScreenState {
  if (installed) return 'installed';
  if (canInstall) return 'installable';
  return settled ? 'manual' : 'detecting';
}
